import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { USER } from 'src/app/core/user.service';
import { AircraftHttpClient } from 'src/app/shared/flight-log-api/aircraft/aircraft-client.service';
import { MaintenanceSummaryDto } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { ConfigurationClassDto } from 'src/app/shared/flight-log-api/maintenance/maintenance-client.model';
import { FlightType } from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { AircraftEntity } from 'src/store/aircraft/aircraft.model';
import { getValidationErrorsChangesDeep } from 'src/util/angular/ng-forms-validation';
import { round } from 'src/util/math';
import { getOrElse } from 'src/util/values';
import { UserViewModel } from 'src/view-models/user-view-model';

@Component({
  selector: 'fl-maintenance-summary',
  templateUrl: './maintenance-summary.component.html',
  styleUrls: ['./maintenance-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceSummaryComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  public maintenanceSummary: FormGroup;
  @Input()
  public isTraining = false;
  @Input()
  public configurationClass: ConfigurationClassDto | undefined;
  @Input()
  public previousMaintenanceTotals: MaintenanceSummaryDto | undefined;
  @Input()
  public aircraftId: string | undefined;
  @Input()
  public flightType?: string;
  @Input()
  public flightLogId: string;

  public validationErrors$: Observable<string[]>;
  public engineCount$ = new BehaviorSubject<number>(0);

  public landingsTotalDisplay$: Observable<string>;
  public landingsTotalTooltip$: Observable<string>;
  public runOnLandingsTotalDisplay$: Observable<string>;
  public runOnLandingsTotalTooltip$: Observable<string>;

  public ngOnChanges$ = new Subject<SimpleChanges>();
  public _onDestroy$ = new Subject();

  get isFlightTypePart133() {
    return this.flightType === FlightType.Part133;
  }

  get totalLandingsCtrl() {
    return this.maintenanceSummary.get('totalLandings') as FormControl;
  }

  get runOnLandingsCtrl() {
    return this.maintenanceSummary.get('runOnLandings') as FormControl;
  }

  get showEngineOne(): boolean {
    const show = this.configurationClass != null && this.configurationClass.engineCount >= 1;
    return show;
  }

  get showEngineTwo(): boolean {
    return this.configurationClass != null && this.configurationClass.engineCount >= 2;
  }

  get totalFlightTimeCtrl() {
    return this.maintenanceSummary.get('totalFlightTime') as FormControl;
  }

  get startingHobbsReadingCtrl() {
    return this.maintenanceSummary.get('startingHobbsReading') as FormControl;
  }

  get endingHobbsReadingCtrl() {
    return this.maintenanceSummary.get('endingHobbsReading') as FormControl;
  }

  get airframeTimeCtrl() {
    return this.maintenanceSummary.get('airframeTime') as FormControl;
  }

  get features() {
    return this._user.flightLogFeatures;
  }

  get previousAirframeTimeCtrl() {
    if (this.previousMaintenanceTotals != null) {
      return new FormControl(round(this.previousMaintenanceTotals.airframeTime, 2));
    } else {
      return new FormControl(0);
    }
  }

  get totalAirframeTimeCtrl() {
    if (this.previousMaintenanceTotals != null && this.airframeTimeCtrl != null) {
      return new FormControl(
        round(this.previousMaintenanceTotals.airframeTime + this.airframeTimeCtrl.value, 2),
      );
    } else {
      return new FormControl(0);
    }
  }

  get netHobbs() {
    let startingHobbs = 0;
    let endingHobbs = 0;

    if (this.startingHobbsReadingCtrl != null) {
      startingHobbs = this.startingHobbsReadingCtrl.value;
    }

    if (this.endingHobbsReadingCtrl != null) {
      endingHobbs = this.endingHobbsReadingCtrl.value;
    }

    const netHobbs = endingHobbs - startingHobbs;
    return new FormControl(round(netHobbs, 2));
  }

  public isAviationSupervisor$: Observable<boolean>;
  public aircraft$: Observable<AircraftEntity | undefined>;

  get previousLandingsTotal() {
    if (this.previousMaintenanceTotals != null) {
      return this.previousMaintenanceTotals.totalLandings;
    }
    return undefined;
  }

  get nextLandingsTotal(): number | undefined {
    if (this.previousLandingsTotal != null) {
      return this.previousLandingsTotal + getOrElse(this.totalLandingsCtrl.value, 0);
    }
    return undefined;
  }

  get previousRunOnLandingsTotal() {
    if (this.previousMaintenanceTotals != null) {
      return this.previousMaintenanceTotals.runOnLandings;
    }
    return undefined;
  }

  get nextRunOnLandingsTotal(): number | undefined {
    if (this.previousMaintenanceTotals != null) {
      return (
        this.previousMaintenanceTotals.runOnLandings + getOrElse(this.runOnLandingsCtrl.value, 0)
      );
    }
    return undefined;
  }

  get landingsTotalDisplay() {
    const prev = getOrElse(this.previousLandingsTotal, '?');
    const next = getOrElse(this.nextLandingsTotal, '?');
    return `(${prev}/${next})`;
  }

  get landingsTotalTooltip() {
    const prev = getOrElse(this.previousLandingsTotal, '?');
    const next = getOrElse(this.nextLandingsTotal, '?');
    return `${prev} previous landings + ${this.totalLandingsCtrl.value} new landings = ${next} total landings`;
  }

  get runOnLandingsTotalDisplay() {
    const prev = getOrElse(this.previousRunOnLandingsTotal, '?');
    const next = getOrElse(this.nextRunOnLandingsTotal, '?');
    return `(${prev}/${next})`;
  }

  get runOnLandingsTotalTooltip() {
    const prev = getOrElse(this.previousRunOnLandingsTotal, '?');
    const next = getOrElse(this.nextRunOnLandingsTotal, '?');
    return `${prev} previous run-on landings + ${this.runOnLandingsCtrl.value} new run-on landings = ${next} total run-on landings`;
  }

  constructor(@Inject(USER) private _user: UserViewModel, private _aircraft: AircraftHttpClient) {
    this.isAviationSupervisor$ = of(_user.isAviationSupervisor);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.ngOnChanges$.next(changes);

    if (changes.aircraftId != null && this.aircraftId != null) {
      this.aircraft$ = this._aircraft.getById(this.aircraftId);
    }

    if (this.configurationClass != null) {
      // tslint:disable-next-line: no-non-null-assertion
      this.engineCount$.next(this.configurationClass!.engineCount);
    }

    this.landingsTotalDisplay$ = this.totalLandingsCtrl.valueChanges.pipe(
      startWith(this.totalLandingsCtrl.value),
      map(() => this.landingsTotalDisplay),
    );

    this.landingsTotalTooltip$ = this.totalLandingsCtrl.valueChanges.pipe(
      startWith(this.totalLandingsCtrl.value),
      map(() => this.landingsTotalTooltip),
    );

    this.runOnLandingsTotalDisplay$ = this.runOnLandingsCtrl.valueChanges.pipe(
      startWith(this.totalLandingsCtrl.value),
      map(() => this.runOnLandingsTotalDisplay),
    );

    this.runOnLandingsTotalTooltip$ = this.runOnLandingsCtrl.valueChanges.pipe(
      startWith(this.totalLandingsCtrl.value),
      map(() => this.runOnLandingsTotalTooltip),
    );

    this.validationErrors$ = getValidationErrorsChangesDeep(this.maintenanceSummary);
  }

  public ngOnInit() {
    this.endingHobbsReadingCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((endingHobbs) => {
        const startingHobbs = this.startingHobbsReadingCtrl.value;
        const totalTime = endingHobbs - startingHobbs;
        this.totalFlightTimeCtrl.patchValue(round(totalTime, 2));
      });

    this.airframeTimeCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((airframeTime) => {
        const prevAirframeTime = this.previousAirframeTimeCtrl.value;
        const totalAirframeTime = prevAirframeTime + airframeTime;
        this.totalAirframeTimeCtrl.patchValue(round(totalAirframeTime, 2));
      });

    const setEngineTime = (engNum: number, val: number) =>
      // tslint:disable-next-line: no-non-null-assertion
      this.maintenanceSummary.get(`engine${engNum}.time`)!.setValue(val);
    this.totalFlightTimeCtrl.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(1000), takeUntil(this._onDestroy$))
      .subscribe((flightTime) => {
        setEngineTime(1, flightTime);
        setEngineTime(2, flightTime);
        this.airframeTimeCtrl.setValue(flightTime);
      });
  }

  public ngOnDestroy() {
    this.ngOnChanges$.complete();
    this._onDestroy$.next();
  }
}
